import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import api from 'api/api';
import useVersionInfo from 'hooks/useVersionInfo';

import { V4NodeScoresResponse } from './types';

// The server hard-422s on the 51st id in a single request, so chunk below it.
export const NODE_SCORES_CHUNK_SIZE = 50;

// Duplicate instances carry composite ids (`${uuid}#${path}`); the endpoint wants the bare uuid and 422s on
// a `#`. Strip to the prefix, drop empties, and dedupe so repeats don't waste the 50-id budget.
export const normalizeNodeIds = (ids: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    const bare = id?.split('#')[0];
    if (!bare || seen.has(bare)) continue;
    seen.add(bare);
    out.push(bare);
  }
  return out;
};

export const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
};

type FetchNodeScoresParams = {
  version: string;
  system_model: string;
  indicatorId: string;
  // Already normalized (bare, unique) by the hook.
  nodeIds: string[];
};

// One POST per ≤50-id chunk in parallel, merged into a single map keyed by the response's `node_id`.
export const fetchNodeScores = async ({
  version,
  system_model,
  indicatorId,
  nodeIds,
}: FetchNodeScoresParams): Promise<Map<string, number | null>> => {
  const responses = await Promise.all(
    chunk(nodeIds, NODE_SCORES_CHUNK_SIZE).map((node_ids) =>
      api.post<V4NodeScoresResponse>(
        '/web/v4/nodes/scores',
        { indicator_id: indicatorId, node_ids },
        { params: { version, system_model } }
      )
    )
  );

  const scoreByNodeId = new Map<string, number | null>();
  for (const { data } of responses) {
    for (const { node_id, score } of data.scores) scoreByNodeId.set(node_id, score);
  }
  return scoreByNodeId;
};

export const nodeScoresQueryKey = (version: string, system_model: string, indicatorId: string, sortedIds: string[]) => [
  'v4NodeScores',
  version,
  system_model,
  indicatorId,
  ...sortedIds,
];

// Shared so a disabled/idle query returns a stable reference instead of a new empty map each render.
const EMPTY_SCORES: Map<string, number | null> = new Map();

export type V4NodeScoresResult = {
  scoreByNodeId: Map<string, number | null>;
  isLoading: boolean;
  isError: boolean;
};

// One request per 50 visible nodes; an expansion or indicator change refetches the whole visible set by
// design (no incremental cache). `staleTime: Infinity` keeps a resolved set to a single fetch.
const useV4NodeScores = (nodeIds: string[], indicatorId: string): V4NodeScoresResult => {
  const { ver: version, system_model } = useVersionInfo();
  const ids = useMemo(() => normalizeNodeIds(nodeIds), [nodeIds]);
  // Sorted so the key is stable regardless of the order nodes arrive in; the request keeps `ids` order.
  const sortedIds = useMemo(() => [...ids].sort(), [ids]);

  const query = useQuery<Map<string, number | null>, AxiosError>({
    queryKey: nodeScoresQueryKey(version, system_model, indicatorId, sortedIds),
    queryFn: () => fetchNodeScores({ version, system_model, indicatorId, nodeIds: ids }),
    enabled: !!indicatorId && ids.length > 0,
    staleTime: Infinity,
    retry: false,
  });

  return {
    scoreByNodeId: query.data ?? EMPTY_SCORES,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

export default useV4NodeScores;
